/**
 * Invoice Service - Generates and manages invoices for payments
 */

import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebase';
import { formatAmount } from './razorpayService';

/**
 * Generate invoice number
 * @returns {string} Invoice number
 */
const generateInvoiceNumber = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const timestamp = Date.now().toString().slice(-6);
  return `INV-${year}${month}-${timestamp}`;
};

/**
 * Create invoice for a payment
 * @param {Object} invoiceData - Invoice details
 * @returns {Promise<Object>} Result with invoice ID
 */
export const createInvoice = async (invoiceData) => {
  try {
    const invoiceNumber = generateInvoiceNumber();

    const invoice = {
      invoiceNumber,
      userId: invoiceData.userId,
      paymentId: invoiceData.paymentId,
      razorpayPaymentId: invoiceData.razorpayPaymentId,
      razorpayOrderId: invoiceData.razorpayOrderId || null,

      // Customer details
      customerName: invoiceData.customerName,
      customerEmail: invoiceData.customerEmail,
      customerPhone: invoiceData.customerPhone,

      // Package details
      packageId: invoiceData.packageId,
      packageName: invoiceData.packageName,
      packageDuration: invoiceData.packageDuration,

      // Amount breakdown
      baseAmount: invoiceData.baseAmount,
      gstRate: invoiceData.gstRate || 18,
      gstAmount: invoiceData.gstAmount,
      totalAmount: invoiceData.totalAmount,
      currency: invoiceData.currency || 'INR',

      // Dates
      invoiceDate: Timestamp.now(),
      paymentDate: invoiceData.paymentDate || Timestamp.now(),

      // Status
      status: 'paid',

      // Additional metadata
      metadata: invoiceData.metadata || {},

      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    const docRef = await addDoc(collection(db, 'invoices'), invoice);

    console.log('✅ Invoice created:', invoiceNumber);

    return {
      success: true,
      invoiceId: docRef.id,
      invoiceNumber,
      invoice
    };
  } catch (error) {
    console.error('❌ Error creating invoice:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get user invoices
 * @param {string} userId - User ID
 * @returns {Promise<Object>} User invoices
 */
export const getUserInvoices = async (userId) => {
  try {
    const q = query(
      collection(db, 'invoices'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const invoices = [];

    querySnapshot.forEach((doc) => {
      invoices.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return { success: true, invoices };
  } catch (error) {
    console.error('❌ Error fetching user invoices:', error);
    return { success: false, error: error.message, invoices: [] };
  }
};

/**
 * Generate invoice HTML for download/print
 * @param {Object} invoice - Invoice data
 * @returns {string} HTML content
 */
export const generateInvoiceHTML = (invoice) => {
  const invoiceDate = invoice.invoiceDate?.toDate?.() || new Date();
  const paymentDate = invoice.paymentDate?.toDate?.() || new Date();

  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Invoice - ${invoice.invoiceNumber}</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      padding: 40px;
      background: #f5f5f5;
    }
    .invoice-container {
      max-width: 800px;
      margin: 0 auto;
      background: white;
      padding: 40px;
      box-shadow: 0 0 10px rgba(0,0,0,0.1);
    }
    .header {
      display: flex;
      justify-content: space-between;
      align-items: start;
      margin-bottom: 40px;
      padding-bottom: 20px;
      border-bottom: 3px solid #4F46E5;
    }
    .company-info h1 {
      color: #4F46E5;
      font-size: 28px;
      margin-bottom: 5px;
    }
    .company-info p {
      color: #666;
      font-size: 14px;
    }
    .invoice-info {
      text-align: right;
    }
    .invoice-info h2 {
      color: #333;
      font-size: 24px;
      margin-bottom: 10px;
    }
    .invoice-info p {
      color: #666;
      font-size: 14px;
      margin: 5px 0;
    }
    .status-badge {
      display: inline-block;
      background: #10B981;
      color: white;
      padding: 5px 15px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: bold;
      margin-top: 10px;
    }
    .customer-details {
      margin: 30px 0;
      padding: 20px;
      background: #f9fafb;
      border-radius: 8px;
    }
    .customer-details h3 {
      color: #333;
      margin-bottom: 10px;
      font-size: 16px;
    }
    .customer-details p {
      color: #666;
      font-size: 14px;
      margin: 5px 0;
    }
    .items-table {
      width: 100%;
      margin: 30px 0;
      border-collapse: collapse;
    }
    .items-table th {
      background: #4F46E5;
      color: white;
      padding: 12px;
      text-align: left;
      font-size: 14px;
    }
    .items-table td {
      padding: 12px;
      border-bottom: 1px solid #e5e7eb;
      font-size: 14px;
    }
    .items-table tr:last-child td {
      border-bottom: none;
    }
    .amount-column {
      text-align: right;
    }
    .totals {
      margin-top: 30px;
      padding-top: 20px;
      border-top: 2px solid #e5e7eb;
    }
    .total-row {
      display: flex;
      justify-content: space-between;
      padding: 8px 0;
      font-size: 14px;
    }
    .total-row.grand-total {
      font-size: 18px;
      font-weight: bold;
      color: #4F46E5;
      padding-top: 15px;
      border-top: 2px solid #4F46E5;
      margin-top: 10px;
    }
    .footer {
      margin-top: 50px;
      padding-top: 20px;
      border-top: 1px solid #e5e7eb;
      text-align: center;
      color: #666;
      font-size: 12px;
    }
    .payment-details {
      margin: 20px 0;
      padding: 15px;
      background: #f0fdf4;
      border-left: 4px solid #10B981;
      border-radius: 4px;
    }
    .payment-details h4 {
      color: #059669;
      margin-bottom: 10px;
      font-size: 14px;
    }
    .payment-details p {
      color: #666;
      font-size: 13px;
      margin: 5px 0;
    }
    @media print {
      body {
        padding: 0;
        background: white;
      }
      .invoice-container {
        box-shadow: none;
      }
    }
  </style>
</head>
<body>
  <div class="invoice-container">
    <!-- Header -->
    <div class="header">
      <div class="company-info">
        <h1>Harshal Jobs</h1>
        <p>Job Portal & Recruitment Services</p>
        <p>Email: support@harshaljobs.com</p>
        <p>Phone: +91 1234567890</p>
      </div>
      <div class="invoice-info">
        <h2>INVOICE</h2>
        <p><strong>Invoice #:</strong> ${invoice.invoiceNumber}</p>
        <p><strong>Date:</strong> ${invoiceDate.toLocaleDateString('en-IN')}</p>
        <p><strong>Payment Date:</strong> ${paymentDate.toLocaleDateString('en-IN')}</p>
        <span class="status-badge">PAID</span>
      </div>
    </div>

    <!-- Customer Details -->
    <div class="customer-details">
      <h3>Bill To:</h3>
      <p><strong>${invoice.customerName}</strong></p>
      <p>Email: ${invoice.customerEmail}</p>
      ${invoice.customerPhone ? `<p>Phone: ${invoice.customerPhone}</p>` : ''}
    </div>

    <!-- Items Table -->
    <table class="items-table">
      <thead>
        <tr>
          <th>Description</th>
          <th>Duration</th>
          <th class="amount-column">Amount</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>
            <strong>${invoice.packageName}</strong>
            <br>
            <small style="color: #666;">Package ID: ${invoice.packageId}</small>
          </td>
          <td>${invoice.packageDuration} days</td>
          <td class="amount-column">${formatAmount(invoice.baseAmount)}</td>
        </tr>
      </tbody>
    </table>

    <!-- Totals -->
    <div class="totals">
      <div class="total-row">
        <span>Subtotal:</span>
        <span>${formatAmount(invoice.baseAmount)}</span>
      </div>
      <div class="total-row">
        <span>GST (${invoice.gstRate}%):</span>
        <span>${formatAmount(invoice.gstAmount)}</span>
      </div>
      <div class="total-row grand-total">
        <span>Total Amount:</span>
        <span>${formatAmount(invoice.totalAmount)}</span>
      </div>
    </div>

    <!-- Payment Details -->
    <div class="payment-details">
      <h4>Payment Information</h4>
      <p><strong>Payment ID:</strong> ${invoice.razorpayPaymentId}</p>
      ${invoice.razorpayOrderId ? `<p><strong>Order ID:</strong> ${invoice.razorpayOrderId}</p>` : ''}
      <p><strong>Payment Method:</strong> Razorpay</p>
      <p><strong>Status:</strong> Paid</p>
    </div>

    <!-- Footer -->
    <div class="footer">
      <p>Thank you for your business!</p>
      <p>This is a computer-generated invoice and does not require a signature.</p>
      <p>For any queries, please contact us at support@harshaljobs.com</p>
    </div>
  </div>
</body>
</html>
  `;
};

/**
 * Download invoice as HTML file
 * @param {Object} invoice - Invoice data
 */
export const downloadInvoice = (invoice) => {
  const html = generateInvoiceHTML(invoice);
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `Invoice-${invoice.invoiceNumber}.html`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Print invoice
 * @param {Object} invoice - Invoice data
 */
export const printInvoice = (invoice) => {
  const html = generateInvoiceHTML(invoice);
  const printWindow = window.open('', '_blank');
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
  }, 250);
};

export default {
  createInvoice,
  getUserInvoices,
  generateInvoiceHTML,
  downloadInvoice,
  printInvoice,
};
